/**
 * ONLY · Ficha de producto (sections/on-main-product.liquid).
 * - Marca de las bandas para el diff por sección según el ancho: «producto» en desktop;
 *   «galeria», «compra» y «barra-de-compra-fija» en mobile (mismo DOM, sin anidar marcas).
 * - Carrusel mobile: barra de progreso y slide actual (aria-current).
 * - Talle: lista propia (botón + listbox, Figma 17:2240) que elige en el <select> oculto; el <select> actualiza la variante del formulario de Horizon (input name="id"),
 *   el precio, la URL (?variant=) y el rótulo. Sin talle elegido, AÑADIR AL CARRITO pone el
 *   selector en rojo (el aviso queda para lectores de pantalla) y no llega a Horizon.
 * - Color: la muestra lleva al mismo talle en el color nuevo; si ahí no está o está agotado (o no
 *   había talle elegido), al color con «SELECCIONAR TALLA».
 * - Guía de talles: el link abre un panel lateral (<dialog>); el selector de tipo de prenda
 *   muestra la guía elegida. Entra como el prototipo (desktop: fundido de 200 ms; mobile: sube
 *   desde abajo en 300 ms) y sale con la misma animación al revés (X, velo o Escape).
 * - Visor: tocar una foto abre un <dialog> con las fotos de la galería, una por vez, con cerrar y
 *   flechas (también ← → del teclado y, en pantallas táctiles, el swipe horizontal).
 * - Compartir: copia la URL de la variante que se está mirando y la etiqueta pasa a «Enlace copiado»
 *   2 s, en desktop y en mobile (Figma 809:3955 / 809:3987).
 * - Descripción / Envíos: pestañas en desktop, acordeones en mobile. En mobile abrir y cerrar anima
 *   250 ms ease-out (Smart Animate del componente «Acordeón de ficha» 11:363, pantalla 807:48276).
 */

const MOBILE = window.matchMedia('(max-width: 749px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const NO_SIZE_KEY = 'on-pdp-nosize';
// La salida más larga de la guía (mobile, 300 ms) y un margen, por si no llega el transitionend.
const GUIDE_MS = 350;

const strings = () => window.OnStrings || {};

/* Copia al portapapeles. Sin la API o sin permiso (iframe, http, navegador que la bloquea) prueba con
   un textarea temporal y execCommand; si tampoco puede, devuelve false y la etiqueta no cambia. */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {}
  const focused = document.activeElement;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.setAttribute('aria-hidden', 'true');
  // 16px para que iOS no haga zoom; fijo arriba para que la página no se mueva.
  area.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;';
  document.body.append(area);
  area.select();
  area.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {}
  area.remove();
  if (focused instanceof HTMLElement && document.activeElement !== focused) focused.focus({ preventScroll: true });
  return ok;
}

class OnProduct extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-on-pdp-track]');
    this.progress = this.querySelector('[data-on-pdp-progress]');
    this.select = this.querySelector('[data-on-pdp-size]');
    this.variantInput = this.querySelector('[data-on-pdp-variant]');
    this.message = this.querySelector('[data-on-pdp-message]');
    this.sizeToggle = this.querySelector('[data-on-pdp-size-toggle]');
    this.sizeList = this.querySelector('[data-on-pdp-size-list]');
    this.zoom = this.querySelector('[data-on-pdp-zoom]');
    this.guide = this.querySelector('[data-on-pdp-guide]');
    this.guideSelect = this.querySelector('[data-on-pdp-guide-select]');
    this.buy = this.querySelector('.on-pdp__buy');
    this.buyMark = this.querySelector('[data-on-pdp-buy-mark]');

    this.onMedia = () => {
      this.markSections();
      this.initTabs();
    };
    MOBILE.addEventListener('change', this.onMedia);
    this.onMedia();

    this.onResize = () => this.placeBuyMark();
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('load', this.onResize, { once: true });

    this.onScroll = () => this.updateProgress();
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });

    this.addEventListener('click', this.onClick, true);
    this.select?.addEventListener('change', this.onSizeChange);
    this.sizeToggle?.addEventListener('keydown', this.onSizeToggleKey);
    this.sizeList?.addEventListener('keydown', this.onSizeListKey);
    document.addEventListener('click', this.onOutsideClick);
    this.track?.addEventListener('keydown', this.onTrackKey);
    this.zoom?.addEventListener('close', this.onZoomClose);
    document.addEventListener('keydown', this.onZoomKey);
    this.zoom?.addEventListener('touchstart', this.onZoomTouchStart, { passive: true });
    this.zoom?.addEventListener('touchend', this.onZoomTouchEnd, { passive: true });
    this.guide?.addEventListener('close', this.onGuideClose);
    this.guide?.addEventListener('cancel', this.onGuideCancel);
    this.guideSelect?.addEventListener('change', this.onGuideChange);

    this.restoreNoSize();
  }

  disconnectedCallback() {
    MOBILE.removeEventListener('change', this.onMedia);
    window.removeEventListener('resize', this.onResize);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('click', this.onClick, true);
    this.select?.removeEventListener('change', this.onSizeChange);
    this.sizeToggle?.removeEventListener('keydown', this.onSizeToggleKey);
    this.sizeList?.removeEventListener('keydown', this.onSizeListKey);
    document.removeEventListener('click', this.onOutsideClick);
    this.track?.removeEventListener('keydown', this.onTrackKey);
    this.zoom?.removeEventListener('close', this.onZoomClose);
    document.removeEventListener('keydown', this.onZoomKey);
    this.zoom?.removeEventListener('touchstart', this.onZoomTouchStart);
    this.zoom?.removeEventListener('touchend', this.onZoomTouchEnd);
    this.guide?.removeEventListener('close', this.onGuideClose);
    this.guide?.removeEventListener('cancel', this.onGuideCancel);
    this.guideSelect?.removeEventListener('change', this.onGuideChange);
    clearTimeout(this.shareTimer);
  }

  markSections() {
    const mobile = MOBILE.matches;
    if (mobile) this.removeAttribute('data-section');
    else this.setAttribute('data-section', this.dataset.sectionDesktop || 'producto');
    this.querySelectorAll('[data-section-mobile]').forEach((el) => {
      if (mobile) el.setAttribute('data-section', el.dataset.sectionMobile);
      else el.removeAttribute('data-section');
    });
    this.placeBuyMark();
  }

  /* La marca de la barra fija copia su caja con la página arriba del todo (donde la dibuja el
     Figma): fondo de la ventana menos el alto de la barra, relativo a la ficha. */
  placeBuyMark() {
    if (!this.buyMark || !this.buy || !MOBILE.matches) return;
    const height = this.buy.offsetHeight;
    const top = document.documentElement.clientHeight - height
      - (this.getBoundingClientRect().top + window.scrollY);
    this.buyMark.style.top = `${Math.round(top)}px`;
    this.buyMark.style.height = `${height}px`;
  }

  updateProgress() {
    if (!this.track || !MOBILE.matches) return;
    const width = this.track.clientWidth || 1;
    const index = Math.round(this.track.scrollLeft / width);
    if (index === this.slideIndex) return;
    this.slideIndex = index;
    this.progress?.parentElement?.style.setProperty('--on-progress', String(index));
    this.track.querySelectorAll('.on-pdp__slide').forEach((slide, i) => {
      if (i === index) slide.setAttribute('aria-current', 'true');
      else slide.removeAttribute('aria-current');
    });
  }

  /* ── Pestañas / acordeones ── */
  get tabs() {
    return Array.from(this.querySelectorAll('[data-on-pdp-tab]'));
  }

  setTab(tab, open, animate = false) {
    tab.setAttribute('aria-expanded', String(open));
    const panel = document.getElementById(tab.getAttribute('aria-controls'));
    if (!panel) return;
    panel.getAnimations?.().forEach((animation) => animation.cancel());
    if (animate && !REDUCED_MOTION.matches && typeof panel.animate === 'function') {
      this.animatePanel(panel, open);
      return;
    }
    panel.hidden = !open;
  }

  /* Acordeón mobile: el Smart Animate de 250 ms ease-out del Figma (11:363) entre Cerrado y Abierto.
     El panel va de 0 a su alto (con los 8 px que lo separan del encabezado) y el texto aparece con un
     fundido; al cerrar, al revés, y recién al terminar queda hidden. */
  animatePanel(panel, open) {
    panel.hidden = false;
    const opened = { height: `${panel.offsetHeight}px`, marginTop: getComputedStyle(panel).marginTop, opacity: 1 };
    const closed = { height: '0px', marginTop: '0px', opacity: 0 };
    panel.style.overflow = 'hidden';
    const animation = panel.animate(open ? [closed, opened] : [opened, closed], { duration: 250, easing: 'ease-out' });
    animation.onfinish = () => {
      panel.style.overflow = '';
      if (!open) panel.hidden = true;
    };
    animation.oncancel = () => {
      panel.style.overflow = '';
    };
  }

  initTabs() {
    const tabs = this.tabs;
    if (MOBILE.matches) {
      tabs.forEach((tab) => this.setTab(tab, true));
    } else {
      const active = tabs.find((tab) => tab.dataset.active === 'true') || tabs[0];
      tabs.forEach((tab) => this.setTab(tab, tab === active));
    }
  }

  onTab(tab) {
    if (MOBILE.matches) {
      this.setTab(tab, tab.getAttribute('aria-expanded') !== 'true', true);
      return;
    }
    this.tabs.forEach((other) => {
      other.dataset.active = String(other === tab);
      this.setTab(other, other === tab);
    });
  }

  /* ── Clicks ── */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-on-pdp-size-toggle]')) {
      if (this.sizeOpen) this.closeSizeList();
      // detail 0: el clic vino de Enter/Espacio sobre el botón, no del mouse.
      else this.openSizeList(event.detail === 0);
      return;
    }

    const sizeOption = target.closest('[data-on-pdp-size-list] [role="option"]');
    if (sizeOption) {
      this.chooseSize(sizeOption);
      return;
    }

    if (this.guide && target.closest('[data-on-pdp-guide-open]')) {
      event.preventDefault();
      this.openGuide();
      return;
    }

    // Cerrar con la X o tocando el velo (el click cae en el propio <dialog>, fuera del panel).
    if (target.closest('[data-on-pdp-guide-close]') || (this.guide && target === this.guide && this.clickOutside(event, this.guide))) {
      this.closeGuide();
      return;
    }

    if (target.closest('[data-on-pdp-zoom-close]')) {
      this.zoom?.close();
      return;
    }

    const step = target.closest('[data-on-pdp-zoom-step]');
    if (step) {
      this.showZoom(this.zoomIndex + Number(step.dataset.onPdpZoomStep));
      return;
    }

    // Con el carrito (u otro diálogo) abierto encima, un click en la foto no abre el visor (Figma 17:1856).
    const slide = target.closest('[data-on-pdp-track] .on-pdp__slide');
    if (slide && slide.querySelector('img') && !this.otherDialogOpen()) {
      this.openZoom(this.slides.indexOf(slide));
      return;
    }

    const tab = target.closest('[data-on-pdp-tab]');
    if (tab) {
      this.onTab(tab);
      return;
    }

    if (target.closest('[data-on-pdp-share]')) {
      this.share();
      return;
    }

    const swatch = target.closest('[data-on-pdp-swatch]');
    if (swatch) {
      this.followSwatch(swatch, event);
      return;
    }

    const add = target.closest('button[name="add"]');
    if (add && this.sizeMissing()) {
      event.preventDefault();
      event.stopPropagation();
      this.askForSize();
    }
  };

  /* ── Guía de talles ──
     Entrada del prototipo (12:694 → 17:2110 desktop: DISSOLVE 200 ms ease-out; 13:624 → 207:24595
     mobile: MOVE_IN de abajo, 300 ms ease-out): .is-open dispara la transición de on-product.css.
     Al cerrar se saca la clase y se espera la transición antes del close() del <dialog>. */
  openGuide() {
    if (!this.guide || this.guide.open) return;
    document.documentElement.classList.add('on-pdp-zoom-open');
    this.guide.showModal();
    // Un reflow antes de la clase para que la entrada (y el velo) animen.
    void this.guide.offsetWidth;
    this.guide.classList.add('is-open');
  }

  async closeGuide() {
    const guide = this.guide;
    if (!guide?.open || this.guideClosing) return;
    this.guideClosing = true;
    guide.classList.remove('is-open');
    if (!REDUCED_MOTION.matches) {
      await new Promise((resolve) => {
        const done = () => {
          guide.removeEventListener('transitionend', onEnd);
          resolve();
        };
        const onEnd = (event) => {
          if (event.target === guide && (event.propertyName === 'opacity' || event.propertyName === 'transform')) done();
        };
        guide.addEventListener('transitionend', onEnd);
        window.setTimeout(done, GUIDE_MS);
      });
    }
    this.guideClosing = false;
    if (guide.open) guide.close();
  }

  // Escape: la misma salida animada. Si el navegador no deja cancelar, cierra solo y limpia onGuideClose.
  onGuideCancel = (event) => {
    if (event.target !== this.guide) return;
    event.preventDefault();
    this.closeGuide();
  };

  /* Limpieza de cualquier cierre (también el que fuerza el navegador). */
  onGuideClose = () => {
    this.guide?.classList.remove('is-open');
    this.onZoomClose();
  };

  onGuideChange = () => {
    const id = this.guideSelect?.value;
    this.guide?.querySelectorAll('[data-on-pdp-guide-item]').forEach((item) => {
      item.hidden = item.id !== id;
    });
  };

  sizeMissing() {
    return Boolean(this.select) && !this.select.value;
  }

  /* ── Cambio de color ──
     El href de la muestra trae el talle con el que se dibujó la página (sin talle elegido, el
     primero: XS), no el que se eligió después en la lista. Al tocarla se apunta a la variante del
     color nuevo con el mismo talle; si no existe o está agotada, o no había talle elegido, al
     color con «SELECCIONAR TALLA» (NO_SIZE_KEY): nunca un talle que la persona no eligió. */
  get variantData() {
    if (this.variantCache === undefined) {
      const script = this.querySelector('[data-on-pdp-variants]');
      try {
        this.variantCache = script ? JSON.parse(script.textContent) : null;
      } catch {
        this.variantCache = null;
      }
    }
    return this.variantCache;
  }

  followSwatch(swatch, event) {
    const data = this.variantData;
    const color = swatch.dataset.color;
    const size = this.sizeMissing() ? null : this.select?.selectedOptions[0]?.dataset.size;
    let keepSize = false;

    if (data && color !== undefined) {
      const current = data.variants.find((variant) => String(variant.id) === this.variantInput?.value);
      // Las demás opciones (si hay una tercera) quedan como la variante actual.
      const sameRest = (variant) =>
        !current || variant.options.every((value, i) => i === data.color || i === data.size || value === current.options[i]);
      const ofColor = data.variants.filter((variant) => variant.options[data.color] === color);
      const sameSize = size
        ? ofColor.find((variant) => variant.available && variant.options[data.size] === size && sameRest(variant))
        : null;
      const next =
        sameSize ||
        ofColor.find((variant) => variant.available && sameRest(variant)) ||
        ofColor.find((variant) => variant.available) ||
        ofColor[0];
      if (next) {
        const url = new URL(swatch.href);
        url.searchParams.set('variant', String(next.id));
        swatch.setAttribute('href', `${url.pathname}${url.search}`);
      }
      keepSize = Boolean(sameSize);
    } else {
      // Sin datos de variantes queda el href del servidor: sólo conserva «SELECCIONAR TALLA».
      keepSize = !this.sizeMissing();
    }

    // Con Cmd/Ctrl/Shift el link se abre aparte: esta pestaña no se toca.
    if (!this.select || keepSize || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    try {
      sessionStorage.setItem(NO_SIZE_KEY, '1');
    } catch {}
  }

  askForSize() {
    const box = this.select.closest('.on-pdp__size');
    this.showMessage(strings().chooseSize || '');
    box?.classList.remove('is-invalid');
    void box?.offsetWidth;
    box?.classList.add('is-invalid');
    this.sizeToggle?.focus({ preventScroll: true });
  }

  showMessage(text) {
    if (this.message) this.message.textContent = text;
  }

  clickOutside(event, dialog) {
    const box = dialog.getBoundingClientRect();
    return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
  }

  /* ── Visor ── */
  otherDialogOpen() {
    return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => !this.contains(dialog));
  }

  get slides() {
    return Array.from(this.track?.querySelectorAll('.on-pdp__slide') || []);
  }

  onTrackKey = (event) => {
    if (event.key !== 'Enter' || event.target !== this.track) return;
    const current = this.track.querySelector('.on-pdp__slide[aria-current="true"]');
    const index = MOBILE.matches && current ? this.slides.indexOf(current) : 0;
    event.preventDefault();
    this.openZoom(index);
  };

  openZoom(index) {
    const stage = this.zoom?.querySelector('[data-on-pdp-zoom-stage]');
    if (!this.zoom || !stage) return;
    const images = this.slides.map((slide) => slide.querySelector('img')).filter(Boolean);
    if (!images.length) return;
    stage.replaceChildren(
      ...images.map((image) => {
        const item = document.createElement('div');
        item.className = 'on-pdp-zoom__slide';
        const copy = image.cloneNode();
        copy.className = 'on-pdp-zoom__image';
        copy.removeAttribute('fetchpriority');
        copy.loading = 'eager';
        copy.sizes = '(min-width: 750px) 864px, 100vw';
        item.append(copy);
        return item;
      })
    );
    this.zoomCount = images.length;
    this.showZoom(Math.max(0, index));
    document.documentElement.classList.add('on-pdp-zoom-open');
    this.zoom.showModal();
  }

  showZoom(index) {
    if (!this.zoom || !this.zoomCount) return;
    this.zoomIndex = Math.min(Math.max(index, 0), this.zoomCount - 1);
    this.zoom.querySelectorAll('.on-pdp-zoom__slide').forEach((item, i) => {
      item.hidden = i !== this.zoomIndex;
    });
    const stage = this.zoom.querySelector('[data-on-pdp-zoom-stage]');
    if (stage) stage.scrollTop = 0;
    const prev = this.zoom.querySelector('.on-pdp-zoom__prev');
    const next = this.zoom.querySelector('.on-pdp-zoom__next');
    if (prev) prev.hidden = this.zoomIndex === 0;
    if (next) next.hidden = this.zoomIndex === this.zoomCount - 1;
  }

  /* ← → pasan de foto, como las flechas del visor. En document: si la flecha que tenía el foco
     se oculta (primera o última foto), el foco sale del <dialog>. */
  onZoomKey = (event) => {
    if (!this.zoom?.open) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    event.preventDefault();
    this.showZoom(this.zoomIndex + (event.key === 'ArrowRight' ? 1 : -1));
  };

  /* Swipe horizontal (50 px, más de lado que de alto) pasa de foto, como la flecha; el vertical
     sigue scrolleando la foto. Con dos dedos (pinza) no hace nada. */
  onZoomTouchStart = (event) => {
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    this.zoomTouch = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  onZoomTouchEnd = (event) => {
    const start = this.zoomTouch;
    this.zoomTouch = null;
    const touch = event.changedTouches[0];
    if (!start || !touch || event.touches.length) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
    this.showZoom(this.zoomIndex + (dx < 0 ? 1 : -1));
  };

  onZoomClose = () => {
    document.documentElement.classList.remove('on-pdp-zoom-open');
  };

  /* ── Talle: lista propia ── */
  get sizeOptions() {
    return Array.from(this.sizeList?.querySelectorAll('[role="option"]') || []);
  }

  get sizeOpen() {
    return Boolean(this.sizeList && !this.sizeList.hidden);
  }

  /* Con el mouse la lista abre blanca y sin fila resaltada (Figma 17:2232); la activa se pinta
     sólo con el teclado (.is-keyboard), para no perder el foco visible. */
  openSizeList(fromKeyboard = false) {
    if (!this.sizeList || !this.sizeToggle) return;
    this.sizeList.classList.toggle('is-keyboard', fromKeyboard);
    this.sizeList.hidden = false;
    this.sizeToggle.setAttribute('aria-expanded', 'true');
    this.classList.add('is-size-open');
    const options = this.sizeOptions;
    const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
    this.setActiveSize(selected || options.find((option) => !this.isSizeDisabled(option)) || options[0]);
    this.sizeList.focus({ preventScroll: true });
  }

  closeSizeList(focusToggle = false) {
    if (!this.sizeOpen) return;
    this.sizeList.hidden = true;
    this.sizeList.removeAttribute('aria-activedescendant');
    this.sizeToggle?.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-size-open');
    if (focusToggle) this.sizeToggle?.focus({ preventScroll: true });
  }

  isSizeDisabled(option) {
    return option.getAttribute('aria-disabled') === 'true';
  }

  setActiveSize(option) {
    if (!option || !this.sizeList) return;
    this.sizeOptions.forEach((other) => other.classList.toggle('is-active', other === option));
    this.sizeList.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  chooseSize(option) {
    if (!option || this.isSizeDisabled(option) || !this.select) return;
    this.sizeOptions.forEach((other) => other.setAttribute('aria-selected', String(other === option)));
    this.select.value = option.dataset.value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.closeSizeList(true);
  }

  onSizeToggleKey = (event) => {
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      this.openSizeList(true);
    }
  };

  onSizeListKey = (event) => {
    const options = this.sizeOptions;
    const index = options.findIndex((option) => option.classList.contains('is-active'));
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) this.sizeList.classList.add('is-keyboard');
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActiveSize(options[Math.min(index + 1, options.length - 1)]);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActiveSize(options[Math.max(index - 1, 0)]);
        break;
      case 'Home':
        event.preventDefault();
        this.setActiveSize(options[0]);
        break;
      case 'End':
        event.preventDefault();
        this.setActiveSize(options[options.length - 1]);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.chooseSize(options[index]);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeSizeList(true);
        break;
      case 'Tab':
        this.closeSizeList();
        break;
    }
  };

  onOutsideClick = (event) => {
    if (!this.sizeOpen) return;
    const box = this.sizeList.closest('.on-pdp__size');
    if (event.target instanceof Node && box?.contains(event.target)) return;
    this.closeSizeList();
  };

  /* ── Talle ── */
  onSizeChange = () => {
    const option = this.select.selectedOptions[0];
    if (!option || !option.value) return;

    if (this.variantInput) {
      this.variantInput.value = option.value;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const box = this.select.closest('.on-pdp__size');
    box?.classList.add('is-chosen');
    box?.classList.remove('is-invalid');
    this.setSizeText(option.dataset.size);
    this.showMessage('');
    this.dataset.sizeRequired = 'false';

    const price = this.querySelector('[data-on-pdp-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    const tax = this.querySelector('[data-on-pdp-tax]');
    if (tax && option.dataset.net && tax.dataset.template) {
      tax.textContent = tax.dataset.template.replace('__PRICE__', option.dataset.net);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    history.replaceState(history.state, '', url.toString());
  };

  setSizeText(size) {
    const label = this.querySelector('[data-on-pdp-size-text]');
    if (!label) return;
    if (!size) {
      label.textContent = label.dataset.placeholder || '';
      return;
    }
    const template = strings().sizeSelected || '{{ size }}';
    label.textContent = template.replace(/\{\{\s*size\s*\}\}/, size);
  }

  restoreNoSize() {
    let flag = null;
    try {
      flag = sessionStorage.getItem(NO_SIZE_KEY);
      sessionStorage.removeItem(NO_SIZE_KEY);
    } catch {}
    if (!flag || !this.select) return;
    this.select.value = '';
    this.sizeOptions.forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.select.closest('.on-pdp__size')?.classList.remove('is-chosen');
    this.setSizeText('');
    this.dataset.sizeRequired = 'true';
  }

  /* ── Compartir ── (Figma 809:3955 desktop · 809:3987 mobile; pantallas 809:39274 y 809:39568)
     Tocarlo copia el enlace y la etiqueta pasa a «ENLACE COPIADO» en el mismo lugar, con el mismo
     ícono y la misma letra. En mobile también: el Figma dibuja ahí el enlace copiado, no la hoja
     nativa de compartir. El aviso para lectores de pantalla va en un role="status" aparte. */
  async share() {
    if (!(await copyText(window.location.href))) return;
    const label = this.querySelector('[data-on-pdp-share-label]');
    const status = this.querySelector('[data-on-pdp-share-status]');
    if (!label) return;
    if (!this.shareText) this.shareText = label.textContent;
    const copied = strings().copied || this.shareText;
    label.textContent = copied;
    if (status) status.textContent = copied;
    clearTimeout(this.shareTimer);
    this.shareTimer = setTimeout(() => {
      label.textContent = this.shareText;
      if (status) status.textContent = '';
    }, 2000);
  }
}

if (!customElements.get('on-product')) customElements.define('on-product', OnProduct);
